import type { AppAction } from './types/ActionTypes'
import {
  BEGIN_POST_LOGIN_OPTIMIZELY_SYNC,
  END_POST_LOGIN_OPTIMIZELY_SYNC,
  SET_OPTIMIZELY_EXPERIMENTS,
  SET_OPTIMIZELY_FEATURE_FLAGS,
  SET_RESOLVED_TRACKING_ID,
} from './actionConstants'
import { rebucketOptimizely } from 'api/optimizely'
import { trackClientEvent } from 'analytics/analytics'
import { reportLog } from 'services/userMonitoring'
import { pickKeys } from 'lib/object/objectUtils'

export function beginPostLoginOptimizelySync() {
  return {
    type: BEGIN_POST_LOGIN_OPTIMIZELY_SYNC,
  } as const
}

export function endPostLoginOptimizelySync() {
  return {
    type: END_POST_LOGIN_OPTIMIZELY_SYNC,
  } as const
}

export function trackPostLoginOptimizelySyncClientEvent(action: string) {
  trackClientEvent({
    subject: 'post_login_sync',
    action,
    category: 'optimizely',
    type: 'nonInteraction',
  })
}

/**
 * Set a single Optimizely experiment (backward compatible)
 *
 * Note: This now uses the bulk update action internally for simplicity.
 * The bulk action can handle single updates just as well.
 */
export function setOptimizelyExperiment(experimentId: string, experiment: App.OptimizelyExperimentDetails, force?: boolean): AppAction {
  return (dispatch, getState) => {
    const state = getState()
    if (!force && state.optimizely.optimizelyExperiments[experimentId]?.triggered) {
      return // Already triggered, don't dispatch again
    }

    // Use bulk update action for consistency
    dispatch({
      type: SET_OPTIMIZELY_EXPERIMENTS,
      experiments: {
        [experimentId]: {
          ...experiment,
          triggered: !force ? true : experiment.triggered,
        },
      },
    })
  }
}

/**
 * Re-bucket Optimizely experiments after user login
 *
 * Calls the server-side re-bucketing API to get updated experiment
 * decisions based on the authenticated user's stitched seed ID.
 *
 * This action:
 * 1. Calls server endpoint (context derived from request on server)
 * 2. Receives updated experiment and feature flag decisions
 * 3. Updates Redux state with new bucketing
 * 4. Tracks re-bucketing event for analytics
 *
 * Fails silently if API call fails - user continues with existing bucketing
 */
export function rebucketOptimizelyExperiments(): AppAction {
  return async(dispatch, getState) => {
    try {
      const previousResolvedId = getState().auth.resolvedTrackingId

      // Call server-side re-bucketing endpoint
      trackPostLoginOptimizelySyncClientEvent('rebucket_request_started')
      const { currentRegionCode } = getState().geo
      const response = await rebucketOptimizely({ regionCode: currentRegionCode })
      trackPostLoginOptimizelySyncClientEvent('rebucket_response_received')

      if (response) {
        const { optimizelyExperiments, optimizelyFeatureFlags, resolvedTrackingId } = response

        // Use fresh state for comparison — state may have changed during the async API call
        // (e.g., experiments triggered while the rebucket request was in-flight)
        const freshState = getState()
        const previousExperiments = freshState.optimizely.optimizelyExperiments

        // Find which experiments changed variation
        const changedExperimentKeys = Object.keys(optimizelyExperiments).filter(
          key => optimizelyExperiments[key]?.variationKey !== previousExperiments[key]?.variationKey,
        )

        // For changed experiments, reset `triggered` to false so useOptimizelyExperiment
        // can re-fire the Snowplow activation event for the new variation
        const changedExperiments = pickKeys(changedExperimentKeys)(optimizelyExperiments)
        const experimentsWithResetTrigger: Record<string, App.OptimizelyExperimentDetails | undefined> = Object.fromEntries(
          Object.entries(changedExperiments).map(([key, experiment]) => [
            key,
            experiment ? { ...experiment, triggered: false } : undefined,
          ]),
        )
        trackPostLoginOptimizelySyncClientEvent('rebucket_decision_diff_completed')

        // Update Redux state with new bucketing decisions
        dispatch({
          type: SET_OPTIMIZELY_EXPERIMENTS,
          experiments: experimentsWithResetTrigger,
        })

        dispatch({
          type: SET_OPTIMIZELY_FEATURE_FLAGS,
          flags: optimizelyFeatureFlags,
        })

        dispatch({
          type: SET_RESOLVED_TRACKING_ID,
          resolvedTrackingId,
        })
        trackPostLoginOptimizelySyncClientEvent('rebucket_state_updated')

        // Log successful re-bucketing to monitoring
        reportLog('Optimizely re-bucketed after login', {
          resolvedTrackingId,
          previousTrackingId: previousResolvedId,
          experimentsChanged: changedExperimentKeys.length,
          totalExperiments: Object.keys(optimizelyExperiments).length,
        }, 'info')
      } else {
        trackPostLoginOptimizelySyncClientEvent('rebucket_response_empty')
      }
    } catch(error) {
      console.error('[Optimizely] Failed to re-bucket experiments:', error)
      // Fail silently - user continues with existing bucketing
      // This ensures login flow isn't blocked by re-bucketing failures
      trackPostLoginOptimizelySyncClientEvent('rebucket_failed')

      reportLog('Optimizely re-bucketing failed', {
        error: error instanceof Error ? error.message : 'Unknown error',
        errorStack: error instanceof Error ? error.stack : undefined,
      })
    }
  }
}
